import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { PartyPopper, Building2, Cake, Heart, Users, CheckCircle, Calendar, Send } from 'lucide-react';

/** Formspree form ID (e.g. xvgkwnno) or full URL https://formspree.io/f/xvgkwnno */
function getFormspreePrivateEventsEndpoint() {
  const raw = (process.env.REACT_APP_FORMSPREE_PRIVATE_EVENTS || '').trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw.replace(/\/$/, '');
  }
  const id = raw.replace(/^\/+/, '');
  return `https://formspree.io/f/${id}`;
}

/** Convert HTML time input (24h "HH:MM") to 12-hour Eastern display, e.g. "5:00 PM ET" */
function formatEventTimeEastern(time24) {
  const trimmed = (time24 || '').trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = Number(match[1]);
  const minutes = match[2];
  if (Number.isNaN(hours) || hours < 0 || hours > 23) return trimmed;

  const period = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;

  return `${hours}:${minutes} ${period} ET`;
}

/** Convert HTML date input ("YYYY-MM-DD") to a readable US date */
function formatEventDateEastern(dateIso) {
  const trimmed = (dateIso || '').trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return trimmed;

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return trimmed;

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/New_York',
  });
}

const Events = () => {
  const { toast } = useToast();
  const formspreeUrl = getFormspreePrivateEventsEndpoint();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_time: '',
    duration: '',
    note: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formspreeUrl) {
      toast({
        title: 'Form unavailable',
        description:
          'Add REACT_APP_FORMSPREE_PRIVATE_EVENTS in Vercel (your Formspree form ID or full https://formspree.io/f/… URL), then redeploy.',
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        _subject: 'Pin High Golf & Lounge - Private Event Inquiry',
        _gotcha: '',
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || '—',
        event_date: formatEventDateEastern(formData.event_date),
        event_time: formatEventTimeEastern(formData.event_time),
        duration: formData.duration.trim(),
        note: formData.note.trim() || '—',
      };
      const res = await fetch(formspreeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        let msg =
          data?.error ||
          (data?.errors &&
            typeof data.errors === 'object' &&
            Object.values(data.errors).flat().filter(Boolean)[0]);
        if (Array.isArray(msg)) msg = msg[0];
        throw new Error(
          typeof msg === 'string' ? msg : `Could not send (${res.status}). Try again or call us.`
        );
      }
      if (data && data.ok === false) {
        throw new Error(data.error || 'Could not send your inquiry.');
      }
      // Formspree returns 200 + { ok: true } on success; treat other 2xx as OK too
      toast({
        title: 'Inquiry sent!',
        description: "We'll get back to you shortly to discuss your event.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        event_date: '',
        event_time: '',
        duration: '',
        note: '',
      });
    } catch (err) {
      toast({
        title: 'Inquiry not sent',
        description: err?.message || 'Please try again in a moment.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    document.title = 'Private Events - Pin High Golf & Lounge | Corporate & Party Venue in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Host your next corporate event, birthday party, or team outing at Pin High Bay Village. Private party room with golf simulators, full catering, and event planning. Book your event today!');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/events');
    updateSocialMeta({
      title: 'Private Events - Pin High Golf & Lounge | Corporate & Party Venue in Bay Village',
      description: 'Host your next corporate event, birthday party, or team outing at Pin High Bay Village. Private party room with golf simulators, full catering, and event planning. Book your event today!',
      path: '/events',
    });
  }, []);

  const eventTypes = [
    {
      title: 'Corporate Events',
      icon: Building2,
      description: 'Team building, client entertainment, and company celebrations',
      features: [
        'Private party room for up to 200 guests',
        'Multiple simulator bays for tournament play',
        'Customizable food and beverage packages',
        'Dedicated event coordinator',
      ],
    },
    {
      title: 'Birthday Parties',
      icon: Cake,
      description: 'Memorable celebrations for all ages',
      features: [
        'Private space for your group',
        'Golf games and challenges',
        'Food and drink packages',
        'Dedicated event coordinator',
      ],
    },
    {
      title: 'Bachelor/Bachelorette',
      icon: Heart,
      description: 'Pre-wedding celebrations with a twist',
      features: [
        'VIP bay access',
        'Flexible time slots',
        'Group discounts available',
        'Dedicated event coordinator',
      ],
    },
    {
      title: 'Social Groups',
      icon: Users,
      description: 'Friend gatherings, reunions, and more',
      features: [
        'Group bay reservations',
        'Tournament format options',
        'Food and beverage service',
        'Flexible scheduling',
        'All skill levels welcome',
        'Dedicated event coordinator',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/private-events-hero.jpeg"
            alt="Private event space at Pin High Golf & Lounge Bay Village"
            className="w-full h-full object-cover opacity-85"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/35"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/40 to-black/25"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <PartyPopper className="w-20 h-20 mx-auto mb-6 text-green-400" />
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Host Your Event at Pin High
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            From corporate team building to birthday celebrations, our private party space and golf simulators create unforgettable experiences for your group.
          </p>
          <p className="text-xl md:text-2xl text-green-400 font-semibold mb-4">
            Call 216-385-4064<br />
            or email: <a href="mailto:kayla@pinhighbay.com" className="text-green-400 hover:text-green-300 hover:underline underline decoration-green-400/70">kayla@pinhighbay.com</a><br />
            <span className="text-lg md:text-xl">for more information</span>
          </p>
          <Button 
            asChild
            size="lg" 
            className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-all duration-300"
          >
            <Link to="/menu">
              Event Menu
            </Link>
          </Button>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Perfect For Every Occasion
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're planning a corporate outing or a celebration with friends, we have the space and expertise to make it memorable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {eventTypes.map((event, index) => {
              const IconComponent = event.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{event.title}</h3>
                        <p className="text-gray-600">{event.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {event.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Why Choose Pin High for Your Event?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <Calendar className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-gray-900">Year-Round Availability</h3>
                <p className="text-gray-600">
                  Never worry about weather. Our climate-controlled indoor space is perfect for events any time of year.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <Users className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-gray-900">All Skill Levels Welcome</h3>
                <p className="text-gray-600">
                  From beginners to scratch golfers, everyone can enjoy our simulators. No experience necessary!
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-gray-900">Full-Service Planning</h3>
                <p className="text-gray-600">
                  Our event coordinators handle all the details so you can focus on enjoying your celebration.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-none shadow-2xl">
            <CardContent className="p-12">
              <div className="flex gap-1 mb-6 justify-center">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-yellow-400 text-2xl">★</span>
                ))}
              </div>
              <p className="text-2xl text-gray-700 mb-8 italic text-center leading-relaxed">
                "We hosted our annual gym group Christmas party there and it was a huge success. The staff handled everything perfectly, the food was great and everyone had a blast on the simulators. We'll definetly be back!"
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  JM
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900 text-lg">Jay M.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Event inquiry form (FastAPI: POST /api/events-inquiry) */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900 text-center">
            Request event info
          </h2>
          <p className="text-xl text-gray-600 mb-8 text-center">
            Share your details and we&apos;ll get back to you with a custom quote.
          </p>
          {!formspreeUrl && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-center">
              Set{' '}
              <code className="text-xs bg-amber-100 px-1 rounded">REACT_APP_FORMSPREE_PRIVATE_EVENTS</code>{' '}
              to your Formspree form ID or full <code className="text-xs bg-amber-100 px-1 rounded">https://formspree.io/f/…</code> URL, then redeploy.
            </p>
          )}
          <Card className="border border-gray-200 shadow-lg">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-gray-900">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-900">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-900">
                    Cell phone
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="(440) 555-1234"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="event_date" className="text-gray-900">
                      Event date *
                    </Label>
                    <Input
                      id="event_date"
                      name="event_date"
                      type="date"
                      required
                      value={formData.event_date}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_time" className="text-gray-900">
                      Time *
                    </Label>
                    <Input
                      id="event_time"
                      name="event_time"
                      type="time"
                      required
                      value={formData.event_time}
                      onChange={handleChange}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration" className="text-gray-900">
                      Duration *
                    </Label>
                    <Input
                      id="duration"
                      name="duration"
                      type="text"
                      required
                      value={formData.duration}
                      onChange={handleChange}
                      className="mt-2"
                      placeholder="e.g., 3 hours"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="note" className="text-gray-900">
                    Note
                  </Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    className="mt-2 min-h-[100px]"
                    placeholder="Group size, type of event, special requests…"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting || !formspreeUrl}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                >
                  {isSubmitting ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send inquiry
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          <p className="text-center text-gray-600 mt-6">
            Or call{' '}
            <a href="tel:2163854064" className="text-green-600 hover:text-green-700 font-semibold">
              216-385-4064
            </a>{' '}
            or email{' '}
            <a href="mailto:kayla@pinhighbay.com" className="text-green-600 hover:text-green-700 font-semibold">
              kayla@pinhighbay.com
            </a>
            .
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Plan Your Event?
          </h2>
          <p className="text-xl mb-8 text-green-50">
            Contact our event team to discuss your vision and get a custom quote.
          </p>
          <p className="text-lg text-green-50 font-bold">
            Call 216-385-4064<br />
            or email: <a href="mailto:kayla@pinhighbay.com" className="text-green-50 hover:text-white hover:underline underline decoration-green-200/70">kayla@pinhighbay.com</a><br />
            for more info
          </p>
        </div>
      </section>
    </div>
  );
};

export default Events;